import React, { useEffect, useState, forwardRef, useImperativeHandle, useRef } from 'react'
import ChatList, { ChatListRef } from './ChatList'
import ChatMessageView from './ChatMessageView'
import { useChatActions } from '@/hooks/useChatActions'
import { SessionType } from '@/api/types/chat'
import { useChatStore } from '@/stores/useChatStore'
import ToastUtils from '@/utils/toast-utils'

// 导出接口以供外部调用
export interface ChatModuleRef {
  navigateToUserChat: (userId: string) => Promise<void>
  navigateToGroupChat: (conversationId: string) => Promise<void>
}

interface ChatModuleProps {
  // 可以添加其他props
}

const ChatModule = forwardRef<ChatModuleRef, ChatModuleProps>((_props, ref): React.JSX.Element => {
  const [selectedConversationId, setSelectedConversationId] = useState<string>('')

  // ChatList 的引用，用于调用滚动方法
  const chatListRef = useRef<ChatListRef>(null)

  const {
    isConversationsLoading,
    createDirectChat,
    getConversations
  } = useChatActions()

  const {
    conversations,
    setActiveConversation,
    clearUnreadCount
  } = useChatStore()

  // console.log("=== 会话：", conversations)

  // 获取当前选中的对话
  const activeConversation = conversations.find((conv: any) => conv.id === selectedConversationId)

  const handleChatSelect = (conversationId: string): void => {
    setSelectedConversationId(conversationId)
    setActiveConversation(conversationId)
    // 清零未读消息计数
    clearUnreadCount(conversationId)
    // 消息加载现在由 ChatMessage 组件内部处理
  }


  // 导航到用户聊天
  const navigateToUserChat = async (userId: string): Promise<void> => {
    try {
      ToastUtils.showLoading(true, "正在打开聊天...")

      // 首先检查是否已经存在与该用户的对话
      let existingConversation = conversations.find((conv: any) =>
        conv.session_type === SessionType.Direct &&
        conv.participants?.some((p: any) => p.id === userId)
      )

      if (existingConversation) {
        // 如果对话已存在，直接跳转
        setSelectedConversationId(existingConversation.id)
        setActiveConversation(existingConversation.id)
        // 清零未读消息计数
        clearUnreadCount(existingConversation.id)

        // 滚动到指定对话
        setTimeout(() => {
          chatListRef.current?.scrollToConversation(existingConversation.id)
        }, 150)

        ToastUtils.showLoading(false)
        return
      }

      // 如果对话不存在，创建新的私聊
      const success = await createDirectChat({ target_id: userId })
      if (success) {
        // 重新获取对话列表以包含新创建的对话
        const updatedConversations = await getConversations()

        // 查找新创建的对话
        const newConversation = updatedConversations?.find(conv =>
          conv.session_type === SessionType.Direct &&
          conv.participants?.some(p => p.id === userId)
        )

        if (newConversation) {
          setSelectedConversationId(newConversation.id)
          setActiveConversation(newConversation.id)
          // 清零未读消息计数
          clearUnreadCount(newConversation.id)

          // 滚动到新创建的对话
          setTimeout(() => {
            chatListRef.current?.scrollToConversation(newConversation.id)
          }, 150)
        } else {
          ToastUtils.showToastFail("无法找到新创建的聊天")
        }
      } else {
        ToastUtils.showToastFail("打开聊天失败")
      }
    } catch (error) {
      console.error('导航到用户聊天失败:', error)
      ToastUtils.showToastFail("打开聊天失败")
    } finally {
      ToastUtils.showLoading(false)
    }
  }

  // 导航到群组聊天
  const navigateToGroupChat = async (conversationId: string): Promise<void> => {
    try {
      ToastUtils.showLoading(true, "正在打开群聊...")

      // 查找对应的对话
      let groupConversation = conversations.find((conv: any) =>
        conv.id === conversationId
      )

      if (groupConversation) {
        setSelectedConversationId(groupConversation.id)
        setActiveConversation(groupConversation.id)
        // 清零未读消息计数
        clearUnreadCount(groupConversation.id)

        // 滚动到指定对话
        setTimeout(() => {
          chatListRef.current?.scrollToConversation(groupConversation!.id)
        }, 150)
      } else {
        // 如果找不到对话，刷新对话列表
        const updatedConversations = await getConversations()
        groupConversation = updatedConversations?.find(conv =>
          conv.id === conversationId
        )

        if (groupConversation) {
          setSelectedConversationId(groupConversation.id)
          setActiveConversation(groupConversation.id)
          // 清零未读消息计数
          clearUnreadCount(groupConversation.id)

          // 滚动到指定对话
          setTimeout(() => {
            chatListRef.current?.scrollToConversation(groupConversation!.id)
          }, 150)
        } else {
          ToastUtils.showToastFail("无法找到群组对话")
        }
      }
    } catch (error) {
      console.error('导航到群组聊天失败:', error)
      ToastUtils.showToastFail("打开群聊失败")
    } finally {
      ToastUtils.showLoading(false)
    }
  }

  // 暴露方法给父组件
  useImperativeHandle(ref, () => ({
    navigateToUserChat,
    navigateToGroupChat
  }))

  useEffect(() => {
    // 只在组件加载时获取会话列表
    // getConversations();

    return () => {
      // 移除当前活跃的会话
      setActiveConversation(null)
    }
  }, [])


  return (
    <div className="h-full flex bg-white">
      {/* 左侧聊天列表 */}
      <ChatList
        ref={chatListRef}
        conversations={conversations}
        activeConversationId={selectedConversationId}
        onChatSelect={handleChatSelect}
        isLoading={isConversationsLoading}
      />

      {/* 右侧聊天区域 */}
      <div className="flex-1 flex">
        {activeConversation ? (
          <>
            <ChatMessageView
              conversation={activeConversation}
            />
          </>
        ) : (
          // 空状态 - 未选择任何对话
          <div className="flex-1 flex items-center justify-center bg-gray-50">
            <div className="text-center">
              <div className="w-24 h-24 mx-auto mb-4 bg-gray-200 rounded-full flex items-center justify-center">
                <svg className="w-12 h-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                </svg>
              </div>
              <p className="text-gray-500 text-lg">暂无对话记录，请选择联系人开始会话。</p>
            </div>
          </div>
        )}
      </div>
    </div>
  )
})

ChatModule.displayName = 'ChatModule'

export default ChatModule
